import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { environment as env } from '../../environments/environment'
import { HEADER_OPTIONS } from '../helpers/helpers'
import { Driver } from '../models/driver.model'
import { TagService } from './tag.service'


@Injectable({
  providedIn: 'root'
})
/**
 * Service responsible for managing driver data within the application.
 *
 * This service provides methods to retrieve, update, and manage the list of drivers,
 * as well as to interact with the backend API for driver-related operations.
 * It uses a `BehaviorSubject` to maintain the current list of drivers and exposes
 * observables for reactive consumption. The service also integrates with the `TagService`
 * to filter drivers based on tags.
 *
 *
 * @example
 * ```typescript
 * constructor(private driverService: DriverService) {}
 *
 * ngOnInit() {
 *   this.driverService.getAll().subscribe(drivers => {
 *     this.driverService.refreshDrivers(drivers);
 *   });
 * }
 * ```
 */
export class DriverService {

  drivers = new BehaviorSubject<Driver.Driver[]>([]);
  getCurrentDriver$: Observable<Driver.Driver[]> = this.drivers
    .asObservable()
    .pipe(map(drivers => this.tagService.filterDrivers(drivers)));

  get allCurrentDrivers() {
    return this.drivers.getValue()
  }

  constructor(public http: HttpClient, private tagService: TagService) {}

  /**
   * Updates the current list of drivers (`drivers`) with a new provided list.
   *
   * The method calculates the difference between the current drivers and the new ones,
   * keeping those drivers that are not present in the new list,
   * and then updates the observable with the combination of the remaining and new drivers.
   *
   * @param drivers - New list of drivers to set as current.
   */
  setCurrentDrivers(drivers: Driver.Driver[]) {
    const diff = this.drivers.getValue().filter(driver => drivers.find(d => d.id === driver.id) === undefined);
    this.drivers.next([...drivers, ...diff]);
  }

  /**
   * Updates the current list of drivers, preserving certain fields from the previous state.
   *
   * For each driver in the new list, if a driver with the same id exists in the current list,
   * the fields `movingTime`, `currentStatus`, and `lastUpdate` from the existing driver are preserved.
   * If the driver does not exist previously, it is added as is.
   *
   * @param drivers New list of drivers to refresh the state with.
   */
  refreshDrivers(drivers: Driver.Driver[]) {
    const lastDrivers = this.drivers.getValue();
    let newDrivers = drivers

    if (lastDrivers.length) {
      newDrivers = newDrivers.map(driver => {
        const existingDriver = lastDrivers.find(d => d.id === driver.id);
        if (existingDriver) {
          return {...driver,
            movingTime: existingDriver?.movingTime || null,
            currentStatus: existingDriver?.currentStatus || null,
            lastUpdate: existingDriver?.lastUpdate || null,
          };
        }
        return driver;
      });
    }

    this.drivers.next(newDrivers);
  }

  /**
   * Updates a specific driver in the current list of drivers.
   *
   * Finds the driver by id and replaces it with the new object provided.
   *
   * @param driver The updated driver to save in the state.
   */
  updateDriver(driver: Driver.Driver) {
    const lastDrivers = this.drivers.getValue();
    const updatedDrivers = lastDrivers.map(d => d.id === driver.id ? driver : d);
    this.drivers.next(updatedDrivers);
  }

  /**
   * Retrieves all drivers from the backend.
   *
   * @returns Observable with the list of drivers.
   */
  getAll() {
    return this.http.get(`${ env.API_PATH }/api/driver/v1/all`, HEADER_OPTIONS());
  }

  /**
   * Retrieves all drivers with scoring from the backend.
   *
   * @returns Observable with the list of drivers with scoring.
   */
  getScoringDrivers() {
    return this.http.get(`${ env.API_PATH }/api/driver/v1/scoring/all`, HEADER_OPTIONS());
  }

}
